import { assign, createMachine } from "xstate";
import {
  IAppointment,
  IReservationMode,
  IReserveAppointmentSlotResponse,
  ISlot,
  reserveAppointmentSlot,
} from "@nju/data-access-portal-appointment";
import { PHONE_NUMBER_REGEX } from "@nju/form";
import { UnknownError } from "@nju/result";
import { generalErrorMessage } from "@nju/utils";
import { resolveAppointmentCodeError } from "./utils";

interface IContext {
  orchestratorId: string;
  phone: string;
  isCalendarVisible: boolean;
  appointmentValue: string;
  mode: IReservationMode;
  slot: ISlot | undefined;
  note: string | undefined;
  phoneError: string | undefined;
  appointmentError: string | undefined;
  formError: string | undefined;
  selectedTimeSlot: IAppointment | undefined;
}

export type IErrorReason =
  | "APPOINTMENT_UNAVAILABLE"
  | "APPOINTMENT_SLOT_TAKEN"
  | "ERROR";

type IEvent =
  | { type: "SET_TIME_SLOT"; value: IAppointment }
  | { type: "SET"; name: "phone" | "note"; value: string }
  | { type: "SUBMIT" }
  | { type: "TOGGLE_CALENDAR" }
  | { type: "CALENDAR_ERROR" }
  | {
      type: "FORM_ERROR";
      reason: IErrorReason;
    }
  | { type: "FORM_SENT"; state: IReserveAppointmentSlotResponse };

const getAppointmentValue = (selectedTimeSlot?: IAppointment): string => {
  if (!selectedTimeSlot || !selectedTimeSlot.slot) {
    return "";
  }

  const { endDate, startDate } = selectedTimeSlot.slot;

  const day = new Date(startDate).toLocaleString("pl-PL", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });

  const start = new Date(startDate).toLocaleString("pl-PL", {
    hour: "numeric",
    minute: "numeric",
  });

  const end = new Date(endDate).toLocaleString("pl-PL", {
    hour: "numeric",
    minute: "numeric",
  });

  return `${day} / ${start}-${end}`;
};

export const appointmentFormMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    entry: "setInitialAppointmentValue",
    initial: "ready",
    states: {
      ready: {
        on: {
          TOGGLE_CALENDAR: {
            actions: "toggleCalendar",
          },
          SET_TIME_SLOT: {
            actions: ["setTimeSlot", "setAppointmentValue"],
          },
          SET: {
            actions: "setValue",
          },
          SUBMIT: {
            target: "validate",
          },
          CALENDAR_ERROR: {
            actions: "setCalendarError",
          },
        },
      },
      validate: {
        entry: "validate",
        always: [
          {
            target: "send",
            cond: "hasNoErrors",
          },
          {
            target: "ready",
          },
        ],
      },
      send: {
        invoke: {
          src: "reserve",
        },
        on: {
          FORM_SENT: [
            {
              cond: "hasAppointmentSuccess",
              target: "appointmentSuccess",
            },
            {
              actions: "onSubmit",
            },
          ],
          FORM_ERROR: [
            {
              cond: "hasAppointmentUnavailable",
              target: "appointmentUnavailable",
            },
            {
              cond: "isAppointmentSlotTaken",
              actions: "setAppointmentSlotTakenError",
              target: "ready",
            },
            {
              actions: "setFormError",
              target: "ready",
            },
          ],
        },
      },
      appointmentUnavailable: {
        type: "final",
      },
      appointmentSuccess: {
        type: "final",
      },
    },
  },
  {
    actions: {
      setInitialAppointmentValue: assign(({ selectedTimeSlot }, _: IEvent) => {
        return {
          appointmentValue: getAppointmentValue(selectedTimeSlot),
        };
      }),
      setValue: assign((context, event: IEvent) => {
        if (event.type === "SET") {
          return {
            [event.name]: event.value,
          };
        }

        return context;
      }),
      setTimeSlot: assign((context, event: IEvent) => {
        if (event.type === "SET_TIME_SLOT") {
          return {
            selectedTimeSlot: event.value,
            appointmentError: undefined,
          };
        }

        return context;
      }),
      setAppointmentValue: assign(({ selectedTimeSlot }, _: IEvent) => {
        return {
          appointmentValue: getAppointmentValue(selectedTimeSlot),
          appointmentError: undefined,
        };
      }),
      toggleCalendar: assign(({ isCalendarVisible }, _: IEvent) => {
        return {
          isCalendarVisible: !isCalendarVisible,
          appointmentError: undefined,
        };
      }),
      setCalendarError: assign(({ isCalendarVisible }, _: IEvent) => {
        return {
          isCalendarVisible: !isCalendarVisible,
          appointmentError: generalErrorMessage,
        };
      }),
      setAppointmentSlotTakenError: assign((_, __: IEvent) => {
        return {
          isCalendarVisible: false,
          appointmentError: "Ten termin został właśnie zajęty, wybierz inny.",
        };
      }),
      setFormError: assign((_, __: IEvent) => {
        return {
          formError: generalErrorMessage,
        };
      }),
      validate: assign((context, _: IEvent) => {
        let phoneError: string | undefined;
        let appointmentError: string | undefined;

        if (!context.phone.trim()) {
          phoneError =
            "Podaj numer telefonu, aby technik mógł się z Tobą skontaktować.";
        } else if (!PHONE_NUMBER_REGEX.test(context.phone.trim())) {
          phoneError = "Numer musi się składać z 9 cyfr.";
        }

        if (!context.selectedTimeSlot?.slot) {
          appointmentError = "Wybierz termin instalacji.";
        }

        return {
          ...context,
          formError: undefined,
          appointmentError,
          phoneError,
        };
      }),
    },
    guards: {
      hasNoErrors: (context) => {
        return (
          context.phoneError === undefined &&
          context.appointmentError === undefined
        );
      },
      hasAppointmentSuccess: (context: IContext) => {
        return context.mode === "CHANGE_FULFILLMENT";
      },
      isAppointmentSlotTaken: (_: IContext, event: IEvent) => {
        if (event.type === "FORM_ERROR") {
          return event.reason === "APPOINTMENT_SLOT_TAKEN";
        }

        return false;
      },
      hasAppointmentUnavailable: (context: IContext, event: IEvent) => {
        if (event.type === "FORM_ERROR") {
          return (
            context.mode === "CHANGE_FULFILLMENT" &&
            event.reason === "APPOINTMENT_UNAVAILABLE"
          );
        }

        return false;
      },
    },
    services: {
      reserve: ({ selectedTimeSlot, phone, note, orchestratorId, mode }) => {
        return async (callback) => {
          if (!selectedTimeSlot || !phone) {
            throw new UnknownError({
              code: "Missing parameters",
              message:
                "selectedTimeSlot and phone number must be selected before request",
            });
          }

          const result = await reserveAppointmentSlot({
            appointmentTimeSlot: selectedTimeSlot.slot,
            /**
             * We have to add country code in order to pass updated validation
             * on API side
             * ```
             * contactNumber must match "\d{11}"
             * ```
             */
            contactNumber: phone.length === 9 ? "48" + phone : phone,
            note,
            orchestratorId,
            mode,
          });

          if (result.isError) {
            return callback({
              type: "FORM_ERROR",
              reason: resolveAppointmentCodeError(result.err.code),
            });
          }

          callback({ type: "FORM_SENT", state: result.value });
        };
      },
    },
  },
);
